import { compose, withProps } from 'recompose';
import { useState, useRef } from 'react';
import {
  GoogleMap,
  Marker,
  InfoWindow,
  withScriptjs,
  withGoogleMap,
  Polygon,
  Polyline,
  OverlayView,
} from 'react-google-maps';
import Spinner from '../Spinner';
import mapPin from '../../../images/map-pin.svg';
import { ILocation } from '../../../entities/general.entities';
import warningIcon from '../../../images/warning-icon.png';
import { LINE_COLORS } from '../../../lib/constant';
import DottedLine from './DottedLine';
import { IMapMarker } from '../../../entities/ui.entities';
import { invertColor } from '../../../lib/common.helpers';

const emptyColor = LINE_COLORS.EMPTY;

interface IPopup {
  position: ILocation;
  onClose: () => void;
  view: any;
}

const defaultCenter = {
  lat: -41.0917585,
  lng: 173.828391,
};

const defaultZoom = 12;

const Loading = () => (
  <div className='mt-20'>
    <Spinner />
  </div>
);

interface MarkerProps {
  marker: IMapMarker;
  popup?: IPopup;
  isShowDottedLine?: (item: IMapMarker) => boolean;
  gBounds?: any;
}

const MapMarker = ({
  marker,
  popup,
  isShowDottedLine,
  gBounds,
}: MarkerProps) => {
  if (marker.type === 'FARM') {
    return marker.bounds && marker.bounds.length > 2 ? (
      <div>
        <Polygon
          path={marker.bounds}
          options={{
            strokeColor: marker.isClosed ? '#000' : '#ff0000',
            strokeOpacity: 1,
            strokeWeight: 1,
            fillOpacity: 0,
          }}
          onClick={e => popup?.onClose()}
        />
        <InfoWindow position={marker.location}>
          <div className='map__pin-label'>{marker.content ?? ''}</div>
        </InfoWindow>
      </div>
    ) : (
      <></>
    );
  } else if (marker.type === 'LINE') {
    return (marker.seedPoints && marker.seedPoints.length > 0) ||
      (marker.points && marker.points.length > 0) ? (
      <div>
        {marker.seedPoints && marker.seedPoints.length > 0 ? (
          marker.seedPoints.map((point, pi) => (
            <div key={`point-${pi}`}>
              {!!point.seed_data &&
                !!marker.lineID &&
                isShowDottedLine &&
                isShowDottedLine(marker) && (
                  <DottedLine
                    lineID={marker.lineID}
                    bounds={gBounds}
                    point={point}
                    seedPositions={marker.seedPoints ?? []}
                  />
                )}
              <Polyline
                path={[point.start, point.end]}
                options={{
                  strokeColor: !point.seed_data
                    ? emptyColor
                    : marker.color ?? 'red',
                  strokeOpacity: 1,
                  strokeWeight: 3,
                }}
                onClick={marker.onClick}
                onMouseOver={e =>
                  marker.onMouseOver
                    ? marker.onMouseOver({
                        lat: e.latLng.lat(),
                        lng: e.latLng.lng(),
                      })
                    : undefined
                }
                onMouseOut={e =>
                  marker.onMouseOut
                    ? marker.onMouseOut({
                        lat: e.latLng.lat(),
                        lng: e.latLng.lng(),
                      })
                    : undefined
                }
              />
            </div>
          ))
        ) : (
          <Polyline
            path={marker.points}
            options={{
              strokeColor: marker.color,
              strokeOpacity: 1,
              strokeWeight: 3,
            }}
            onClick={marker.onClick}
            onMouseOver={e =>
              marker.onMouseOver
                ? marker.onMouseOver({
                    lat: e.latLng.lat(),
                    lng: e.latLng.lng(),
                  })
                : undefined
            }
            onMouseOut={e =>
              marker.onMouseOut
                ? marker.onMouseOut({
                    lat: e.latLng.lat(),
                    lng: e.latLng.lng(),
                  })
                : undefined
            }
          />
        )}
        {!!marker.content && (
          <OverlayView
            position={marker.location}
            mapPaneName={OverlayView.OVERLAY_LAYER}
          >
            <div style={{ color: marker.color, fontWeight: 'bold' }}>
              {marker.content}
            </div>
          </OverlayView>
        )}
        {!!marker.urgent && (
          <OverlayView
            position={marker.urgent}
            mapPaneName={OverlayView.OVERLAY_LAYER}
          >
            <div
              style={{
                position: 'absolute',
                transform: 'translate(-50%, -100%)',
              }}
            >
              <img
                style={{ width: '25px', height: '25px' }}
                src={warningIcon}
                alt='warning'
              />
            </div>
          </OverlayView>
        )}
      </div>
    ) : (
      <></>
    );
  } else if (marker.type === 'TRACK') {
    return marker.tracks && marker.tracks.length > 0 ? (
      <div>
        <Polyline
          path={marker.tracks}
          options={{
            strokeColor: '#ff0000',
            strokeOpacity: 1,
            strokeWeight: 2,
          }}
        />
        <InfoWindow position={marker.location}>
          {marker.content ?? ''}
        </InfoWindow>
      </div>
    ) : (
      <></>
    );
  } else if (marker.type === 'RAFT') {
    return marker.points && marker.points.length > 2 ? (
      <div>
        <Polygon
          path={marker.points}
          options={{
            strokeColor: '#000000',
            strokeOpacity: 1,
            strokeWeight: 1,
            fillColor: marker.color ?? '#ff0000',
            fillOpacity: 1,
          }}
          onClick={marker.onClick}
          onMouseOver={e =>
            marker.onMouseOver
              ? marker.onMouseOver({
                  lat: e.latLng.lat(),
                  lng: e.latLng.lng(),
                })
              : undefined
          }
          onMouseOut={e =>
            marker.onMouseOut
              ? marker.onMouseOut({
                  lat: e.latLng.lat(),
                  lng: e.latLng.lng(),
                })
              : undefined
          }
        />
        {!!marker.content && (
          <OverlayView
            position={marker.location}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div
              style={{
                color: invertColor(marker.color ?? '#ff0000'),
                fontWeight: 'bold',
              }}
            >
              {marker.content}
            </div>
          </OverlayView>
        )}
        {!!marker.urgent && (
          <OverlayView
            position={marker.urgent}
            mapPaneName={OverlayView.OVERLAY_LAYER}
          >
            <div
              style={{
                position: 'absolute',
                transform: 'translate(-50%, -100%)',
              }}
            >
              <img
                style={{ width: '25px', height: '25px' }}
                src={warningIcon}
                alt='warning'
              />
            </div>
          </OverlayView>
        )}
      </div>
    ) : (
      <></>
    );
  } else {
    return (
      <Marker
        position={marker.location}
        icon={{
          url: mapPin,
        }}
      >
        <InfoWindow>
          <div className='map__pin-label'>{marker.content ?? ''}</div>
        </InfoWindow>
      </Marker>
    );
  }
};

interface Props {
  center?: ILocation;
  zoom?: number;
  markers: IMapMarker[];
  mapTypeId?: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
  popup?: IPopup;
}

const MarkersMap = compose<Props, Props>(
  withProps({
    googleMapURL: `https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`,
    loadingElement: <Loading />,
    containerElement: (
      <div style={{ width: '100%', height: `100%`, position: 'absolute' }} />
    ),
    mapElement: (
      <div style={{ width: '100%', height: `100%`, position: 'relative' }} />
    ),
  }),
  withScriptjs,
  withGoogleMap,
)(({ zoom, center, markers, mapTypeId, popup }) => {
  const mapRef = useRef<any>(null);
  const [zoomed, setZoomed] = useState<number | undefined>(zoom);
  const [gBounds, setGBounds] = useState<any | null>(null);

  const isShowDottedLine = (item: IMapMarker) => {
    if (!zoomed || zoomed <= 5 || !gBounds) {
      return false;
    }
    if (!item.points || item.points.length < 2) {
      return false;
    }
    return true;
  };

  return (
    <GoogleMap
      ref={mapRef}
      options={{
        minZoom: 3,
        maxZoom: 50,
        zoomControl: true,
      }}
      mapTypeId={mapTypeId}
      defaultZoom={zoom ?? defaultZoom}
      defaultCenter={center ?? defaultCenter}
      center={center ?? defaultCenter}
      zoom={zoom ?? defaultZoom}
      onClick={e => popup?.onClose()}
      onZoomChanged={() => {
        const currentZoom = mapRef.current?.getZoom();
        if (currentZoom !== undefined) {
          setZoomed(currentZoom);
        }
      }}
      onBoundsChanged={() => {
        const newBounds = mapRef.current?.getBounds();
        if (newBounds) {
          setGBounds(newBounds);
        }
      }}
    >
      {!!popup && (
        <InfoWindow position={popup.position}>{popup.view}</InfoWindow>
      )}
      {markers.map((marker, idx) => (
        <MapMarker
          key={idx}
          marker={marker}
          popup={popup}
          isShowDottedLine={isShowDottedLine}
          gBounds={gBounds}
        />
      ))}
    </GoogleMap>
  );
});

export default MarkersMap;
